import * as React from "react";
import {useTheme} from "@mui/material/styles";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import {
  AppBar,
  Card,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Box,
  IconButton
} from "@mui/material";
import {
  LastPage,
  FirstPage,
  NavigateBefore,
  NavigateNext
} from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {GET} from "APIclient";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import * as XLSX from "xlsx";
import {formatDesignation} from "constants/ApplicationConstants";
import properties from "constants/ApplicationConstants";

const rowLength = 20;
const staticTechnologies = [
  "Java",
  "QA",
  "C",
  "C++",
  "Python",
  "DOT NET",
  "SALESFORCE",
  "IOS",
  "ANDROID",
  "PHP",
  "Business Development",
  "Human Resources",
  "IT"
];

function CustomPagination({ 
  page, 
  pageSize, 
  totalRows, 
  totalPages, 
  onPageChange, 
  onGoToLastPage,
  loading 
}) {
  const handleFirstPage = () => {
    onPageChange(0);
  };

  const handlePreviousPage = () => {
    onPageChange(Math.max(0, page - 1));
  };

  const handleNextPage = () => {
    onPageChange(Math.min(totalPages - 1, page + 1));
  };

  const handleLastPage = () => {
    onGoToLastPage();
  };

  const startRow = page * pageSize + 1;
  const endRow = Math.min((page + 1) * pageSize, totalRows);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1,
        borderTop: '1px solid #e0e0e0'
      }}
    >
      <Typography variant="caption" sx={{ color: '#666' }}>
        {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : 'No rows'}
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ mr: 2, color: '#666' }}>
          Page {page + 1} of {totalPages || 1}
        </Typography>
        
        <Tooltip title="First Page">
          <IconButton
            onClick={handleFirstPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <FirstPage />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Previous Page">
          <IconButton
            onClick={handlePreviousPage}
            disabled={page === 0 || loading}
            size="small"
          >
            <NavigateBefore />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Next Page">
          <IconButton
            onClick={handleNextPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
          >
            <NavigateNext />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Last Page">
          <IconButton
            onClick={handleLastPage}
            disabled={page >= totalPages - 1 || loading}
            size="small"
            sx={{
              backgroundColor: properties?.colors?.bgcolorbluethem || '#1976d2',
              color: 'white',
              '&:hover': {
                backgroundColor: properties?.colors?.bgcolorbluethem || '#1565c0',
              },
              '&.Mui-disabled': {
                backgroundColor: '#e0e0e0',
                color: '#9e9e9e'
              }
            }}
          >
            <LastPage />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function TechnologyList() {
  const theme = useTheme();
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(rowLength);
  const [searchId, setSearchId] = React.useState("");
  const [searchName, setSearchName] = React.useState("");
  const [searchTech, setSearchTech] = React.useState("");
  const [filteredData, setFilteredData] = React.useState([]);
  const [totalRows, setTotalRows] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);

  const fetchData = async (page, pageSize, empId, empName, technology) => {
    try {
      setLoading(true);
      
      const queryParams = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString()
      });
      
      if (empId) queryParams.append('empId', empId);
      if (empName) queryParams.append('empName', empName);
      if (technology) queryParams.append('technology', technology);

      const response = await GET(`admin/technology-list?${queryParams.toString()}`);
      const responseData = response?.data?.data?.paginatedData || [];

      const mappedData = responseData.map((item, index) => ({
        ...item,
        id: page * pageSize + index + 1,
        designation: formatDesignation(item?.designation)
      }));

      setData(mappedData);
      setFilteredData(mappedData);
      setTotalRows(response?.data?.data?.totalItems || 0);
      setTotalPages(response?.data?.data?.totalPages || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData(page, pageSize, searchId, searchName, searchTech);
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(0);
    fetchData(0, pageSize, searchId, searchName, searchTech);
  };

  const handleKeyDown = event => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleDownloadExcel = () => {
    const exportData = filteredData.map(row => ({
      ...row,
      primarySkillset: Array.isArray(row.primarySkillset)
        ? row.primarySkillset.join(", ")
        : row.primarySkillset,
      secondarySkillset: Array.isArray(row.secondarySkillset)
        ? row.secondarySkillset.join(", ")
        : row.secondarySkillset
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Technology Report List");
    XLSX.writeFile(workbook, "TechnologyReport_List.xlsx");
  };

  const handlePaginationChange = paginationModel => {
    setPage(paginationModel.page);
    setPageSize(paginationModel.pageSize);
     setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage, pageSize, searchId, searchName, searchTech);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  };

  const handleGoToLastPage = async () => {
    if (totalPages > 0) {
      const lastPageIndex = totalPages - 1;
      setPage(lastPageIndex);
      await fetchData(lastPageIndex, pageSize, searchId, searchName, searchTech);
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 0);
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "#",
      width: 30,
      align: "left",
      headerAlign: "left"
    },
    {field: "empId", headerName: "EMP ID", width: 80, align: "left", headerAlign: "left"},
    {
      field: "empName",
      headerName: "Employee Name",
      width: 200,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "designation",
      headerName: "Designation",
      width: 220,
      align: "left",
      headerAlign: "left"
    },
    {
      field: "primarySkillset",
      headerName: "Primary Skill",
      width: 240,
      align: "left",
      headerAlign: "left",
      renderCell: params => {
        const skills = params.row.primarySkillset || [];
        const maxVisible = 3;
        const visibleSkills = skills.slice(0, maxVisible);
        const hiddenSkills = skills.slice(maxVisible);
        return (
          <MDBox
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "6px",
              minHeight: "32px"
            }}
          >
            {visibleSkills.map((skill, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  display: "inline-block",
                  fontSize: "11px",
                  padding: "2px 10px",
                  backgroundColor: theme.palette.success.light,
                  borderRadius: "8px",
                  color: theme.palette.common.white,
                  lineHeight: 1.5
                }}
              >
                {skill}
              </Typography>
            ))}
            {hiddenSkills.length > 0 && (
              <Tooltip title={skills.join(", ")} placement="top">
                <Typography
                  variant="body2"
                  sx={{
                    display: "inline-block",
                    fontSize: "11px",
                    padding: "2px 10px",
                    backgroundColor: theme.palette.grey[500],
                    borderRadius: "8px",
                    color: theme.palette.common.white,
                    lineHeight: 1.5,
                    cursor: "pointer"
                  }}
                >
                  +{hiddenSkills.length} more
                </Typography>
              </Tooltip>
            )}
          </MDBox>
        );
      }
    },
    {
      field: "secondarySkillset",
      headerName: "Secondary Skill",
      width: 220,
      align: "left",
      headerAlign: "left",
      renderCell: params => {
        const skills = params.row.secondarySkillset || [];
        const maxVisible = 3;
        const visibleSkills = skills.slice(0, maxVisible);
        const hiddenSkills = skills.slice(maxVisible);
        return (
          <MDBox
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "6px",
              minHeight: "32px"
            }}
          >
            {visibleSkills.map((skill, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{
                  display: "inline-block",
                  fontSize: "11px",
                  padding: "2px 10px",
                  backgroundColor: theme.palette.info.light,
                  borderRadius: "8px",
                  color: theme.palette.common.white,
                  lineHeight: 1.5
                }}
              >
                {skill}
              </Typography>
            ))}
            {hiddenSkills.length > 0 && (
              <Tooltip title={skills.join(", ")} placement="top">
                <Typography
                  variant="body2"
                  sx={{
                    display: "inline-block",
                    fontSize: "11px",
                    padding: "2px 10px",
                    backgroundColor: theme.palette.grey[500],
                    borderRadius: "8px",
                    color: theme.palette.common.white,
                    lineHeight: 1.5,
                    cursor: "pointer"
                  }}
                >
                  +{hiddenSkills.length} more
                </Typography>
              </Tooltip>
            )}
          </MDBox>
        );
      }
    }
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <AppBar position="static">
          <Card>
            <MDBox pt={2} px={3} pb={2}>
              <MDBox display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={2}>
                <Typography
                  sx={{
                    gridColumn: "span 2",
                    color: "textPrimary",
                    fontFamily: properties.fontFamily,
                    fontSize: "15px",
                    fontWeight: "bold"
                  }}
                >
                  Technology Report
                </Typography>
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  label="Employee ID"
                  value={searchId}
                  onKeyDown={handleKeyDown}
                  onChange={e => setSearchId(e.target.value)}
                />
                <MDInput
                  sx={{gridColumn: "span 2"}}
                  label="Employee Name"
                  value={searchName}
                  onKeyDown={handleKeyDown}
                  onChange={e => setSearchName(e.target.value)}
                />
                <FormControl sx={{gridColumn: "span 2"}} fullWidth>
                  <InputLabel>Technology</InputLabel>
                  <Select
                    value={searchTech}
                    onChange={e => setSearchTech(e.target.value)}
                    label="Skill"
                    onKeyDown={handleKeyDown}
                    sx={{
                      height: "43px"
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {staticTechnologies.map((tech, index) => (
                      <MenuItem key={index} value={tech}>
                        {tech}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <MDButton
                  sx={{gridColumn: "span 2", background: properties.colors.bgcolorbluethem}}
                  variant="gradient"
                  color="info"
                  onClick={handleSearch}
                >
                  Search
                </MDButton>
                <MDButton
                  sx={{gridColumn: "span 2"}}
                  variant="gradient"
                  color="success"
                  onClick={handleDownloadExcel}
                >
                  Download
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </AppBar>
            <MDBox>
              <Paper>
                <DataGrid
                  rows={filteredData}
                  columns={columns}
                  paginationMode="server"
                  rowCount={totalRows}
                  pageSizeOptions={[20, 20, 50]}
                  paginationModel={{page, pageSize}}
                  onPaginationModelChange={handlePaginationChange}
                  loading={loading}
                  components={{Toolbar: GridToolbar}}
                  hideFooter
                  sx={{
                    height: "450px",
                    fontWeight:400,
                    fontFamily: properties.fontFamily,
                    fontSize: "14px",
                    "& .MuiDataGrid-cell": {
                      textAlign: "left"
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      textAlign: "left"
                    },
                    "& .MuiDataGrid-columnHeader": {
                      textAlign: "left"
                    },
                    "& .MuiDataGrid-footerContainer": {
                      justifyContent: "flex-start"
                    },
                    "& .MuiDataGrid-scrollbar": {
                      width: "10px !important",
                    },
                  }}
                />
                <CustomPagination
                  page={page}
                  pageSize={pageSize}
                  totalRows={totalRows}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  onGoToLastPage={handleGoToLastPage}
                  loading={loading}
                />
              </Paper>
            </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}